/* eslint-disable @typescript-eslint/no-explicit-any */

import { GetServerSideProps, GetServerSidePropsContext } from 'next/types';
import { serverSideTranslations } from '@utils/i18n/serverSideTranslations';
import { ReactElement, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { TLocale, ISeo } from '@typescript/index';
import getOffer from '@utils/getOffer';
import { ICategory, IListingData, TCategoryDataFiles, TCategoriesTree, IArrangementProduct, IVirtualCategory, ICategorySeo, IVirtualCategoryFilters, INavigationVirtualCategory } from '@features/categories';
import { getLayoutData, getMarketingCategoriesData } from '@features/layout/api';
import { ENavTransparency } from '@features/navigation/typescript';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import { IBlocksData, ICollectionPageProps, TCmsBlocks } from '@features/cms/typescript';
import { getCategoryListingPageProps } from '@utils/pages/categoryListingPageHelper';
import { getCollectionPageProps } from '@utils/pages/collectionPageHelper';
import getArrangementProducts from '@utils/getArrangementProducts';
import getVirtualCategories from '@utils/getVirtualCategories';
import getVirtualCategoryBySlug from '@utils/getVirtualCategoryBySlug';
import getNavigationVirtualCategories from '@utils/getNavigationVirtualCategories';
import CollectionPage from '@features/categories/components/CollectionPage/CollectionPage';
import CollectionLayout from '@features/layout/components/Layout/components/CollectionLayout/CollectionLayout';
import Layout from '@features/layout/components/Layout/Layout';
import { PUBLIC_HOST, REVALIDATION_TIME } from '@config/index';
import CategoriesSeoPaginationLinks from '@features/categories/components/CategoriesSeoPaginationLinks/CategoriesSeoPaginationLinks';
import SelfCanonicalLink from '@features/seo/SelfCanonicalLink/SelfCanonicalLink';
import { getDevProps } from '@components/DevToolbar/helpers/getDevProps';
import CategoryListingPage from '@features/categories/components/CategoryListingPage/CategoryListingPage';
import { IInfoCardWithMedia } from '@components/typescript';
import { getCache, setCache } from '@utils/cacheHelpers';
import { trackRevalidation } from '@utils/revalidationTracker';
import logger from '@utils/logger';
import { getCategoryListingDisplayName } from '@utils/getCategoryListingDisplayName';
type PropsType = {
  categoryName: string;
  categoryId: number;
  listingData: IListingData;
  isCollectionPage: boolean;
  categoryFiles: TCategoryDataFiles;
  categoriesTree: TCategoriesTree;
  categoryChildren: Record<symbol, ICategory>;
  content?: Record<string, TCmsBlocks>;
  pageContent?: Record<string, TCmsBlocks>;
  categoryBlocks?: IBlocksData;
  seo: ISeo;
  fullPath: string;
  arrangementProducts: IArrangementProduct[];
  infoCards: IInfoCardWithMedia[];
  showProducts: boolean;
  navigationVirtualCategories: INavigationVirtualCategory[];
  virtualCategoryFilters: IVirtualCategoryFilters;
  virtualCategorySlug: string | null;
};
const MainCategoryDynamic = ({
  categoryName,
  categoryId,
  isCollectionPage,
  categoryFiles,
  categoriesTree,
  listingData,
  categoryChildren,
  content = undefined,
  categoryBlocks = undefined,
  pageContent = undefined,
  seo,
  fullPath,
  arrangementProducts,
  infoCards,
  showProducts,
  navigationVirtualCategories,
  virtualCategoryFilters,
  virtualCategorySlug
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    setLastVisitedCategoryId
  } = layoutUiState;
  const router = useRouter();
  const pageValue = router.query.page;
  const getCategoryFullSlug = (): string => {
    const {
      locale,
      query,
      defaultLocale
    } = router;
    let categoryFullSlug = [query.mainCategory, ...(query?.slug?.length ? query.slug.slice(0, 1) : [])].join('/');
    if (locale !== defaultLocale) {
      categoryFullSlug = `${locale}/${categoryFullSlug}`;
    }
    if (virtualCategorySlug && virtualCategorySlug !== 'products') {
      categoryFullSlug += `/${virtualCategorySlug}`;
    }
    if (query?.page && query?.page !== '1') {
      categoryFullSlug += `?page=${query.page}`;
    }
    return categoryFullSlug;
  };
  const canonicalLink = useMemo(() => {
    const currentPath = `${PUBLIC_HOST}/${getCategoryFullSlug()}`;
    return <SelfCanonicalLink currentUrl={currentPath} />;
  }, [router.asPath]);
  useEffect(() => {
    setLastVisitedCategoryId(categoryId);
  }, [categoryId]);
  const seoObject = {
    ...seo,
    title: seo?.title && pageValue !== '1' && pageValue ? `${seo.title} - ${t('page')} ${pageValue}` : `${seo.title}`
  };
  return <>
      {canonicalLink}
      <main id="main-content">
        <CategoriesSeoPaginationLinks paginationCount={listingData?.pagination?.count || 0} currentUrl={fullPath} />
        {isCollectionPage ? <CollectionPage content={showProducts ? {} : pageContent || content} seo={seoObject} /> : <CategoryListingPage categoryName={categoryName} categoryId={categoryId} categoryFiles={categoryFiles} categoriesTree={categoriesTree} listingData={listingData} categoryChildren={categoryChildren} arrangementProducts={arrangementProducts} categoryBlocks={categoryBlocks ? categoryBlocks.blocks : null} infoCards={infoCards} seo={seoObject} virtualCategoryFilters={virtualCategoryFilters} navigationVirtualCategories={navigationVirtualCategories} virtualCategorySlug={virtualCategorySlug} />}
      </main>
    </>;
};
export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const {
    params,
    locale,
    resolvedUrl
  } = context;
  let page = 1;
  const filters = {};
  let sorting = '';
  const isRevalidationRequest = context.req.headers['x-revalidation-token'] === process.env.CUSTOM_CACHE_MANAGEMENT_TOKEN && context.req.headers['x-revalidation'] === 'true';
  const revalidationType = isRevalidationRequest ? context.req.headers['x-revalidation-type'] : undefined;
  const [offer] = getOffer();
  if (resolvedUrl.includes('?')) {
    const queryString = resolvedUrl.split('?')[1] || '';
    const queryParams = new URLSearchParams(queryString);
    page = parseInt(queryParams.get('page') || '1', 10);

    // Check if we need to redirect due to old filter format
    const needsRedirect = ['color[]', 'collection[]', 'product_subtype[]', 'product_type[]'].some(oldFilter => queryParams.has(oldFilter));
    if (needsRedirect) {
      const newQueryParams = new URLSearchParams();

      // Zachowaj parametry śledzenia Google Ads
      const trackingParamNames = ['gclid', 'glcid', 'gclsrc', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'msclkid', 'ref'];
      trackingParamNames.forEach(param => {
        const value = queryParams.get(param);
        if (value) {
          newQueryParams.set(param, value);
        }
      });

      // Przetwórz filtry
      queryParams.forEach((value, key) => {
        let newKey = key;
        if (key === 'color[]') newKey = 'filter.color[]';else if (key === 'collection[]') newKey = 'filter.collection[]';else if (key === 'product_subtype[]') newKey = 'filter.product_subtype[]';else if (key === 'product_type[]') newKey = 'filter.product_type[]';else if (!trackingParamNames.includes(key)) {
          // Zachowaj inne parametry, które nie są parametrami śledzenia
          newQueryParams.append(key, value);
          return;
        }
        newQueryParams.append(newKey, value);
      });
      return {
        redirect: {
          destination: `${resolvedUrl.split('?')[0]}?${newQueryParams.toString()}`,
          permanent: true
        }
      };
    }

    // Process filters normally if no redirect needed
    queryParams.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        filters[key.replace('filter.', '').replace('[]', '')] = value;
      }
    });
    const sortParam = queryParams.get('sort');
    sorting = sortParam?.includes('_') ? sortParam : '';
  }
  const {
    slug = [],
    mainCategory = ''
  } = params as {
    slug: string[];
    mainCategory: string;
  };
  const layoutData = await getLayoutData(locale as TLocale);
  const {
    marketingCategoriesServiceMap,
    flatCategoriesTree
  } = await getMarketingCategoriesData(locale as TLocale);
  const slugArray = [mainCategory, ...slug];
  const fullPath = slugArray.join('/');
  const path = [slugArray[0], slugArray[1]].join('/');
  const cacheKey = `${path.replace(/\//g, '-')}-${page}-${locale}`;
  const cachePropsTime = process.env.CATEGORIES_PROPS_REVALIDATION_TIME ? parseInt(process.env.CATEGORIES_PROPS_REVALIDATION_TIME, 10) : 600;
  const categoryPageSlug = slugArray.length > 1 ? [slugArray[0], slugArray[1]].join('/') : mainCategory;
  const serviceMapObject = marketingCategoriesServiceMap[fullPath] || marketingCategoriesServiceMap[path];
  if (!serviceMapObject) {
    // biome-ignore lint/suspicious/noConsole: intentional
    console.log(`serviceMapObject not found for ${fullPath} --`);
    return {
      props: {},
      notFound: true
    };
  }
  let virtualCategorySlug = offer === 'VOX-PL-ECOMMERCE' && slugArray.length > 2 ? slugArray[2] : null;
  const isCollection = serviceMapObject.behaviour === 'collection' || serviceMapObject.behaviour === 'collectionContent';
  const foundCategoryId = serviceMapObject.id;
  const category: ICategory = flatCategoriesTree[foundCategoryId];
  const categoryChildren: Record<symbol, ICategory> = {};
  if (Array.isArray(category.children) && category.children.length) {
    category.children.forEach(id => {
      if (id) categoryChildren[id] = flatCategoriesTree[id];
    });
  }
  let categoryPageProps = {};
  let virtualCategories: IVirtualCategory[] | null = null;
  if (revalidationType === 'VirtualCategories' || revalidationType === 'full') {
    virtualCategories = await getVirtualCategories(category);
    await setCache(`${cacheKey}-VirtualCategories`, virtualCategories, cachePropsTime, true);
  } else {
    virtualCategories = await getCache(`${cacheKey}-VirtualCategories`, () => getVirtualCategories(category), cachePropsTime, true);
  }
  let virtualCategory: IVirtualCategory | null = null;
  if (offer === 'VOX-PL-ECOMMERCE' && virtualCategorySlug && virtualCategorySlug !== 'products' && Array.isArray(virtualCategories) && virtualCategories.length) {
    virtualCategory = getVirtualCategoryBySlug(virtualCategories, virtualCategorySlug) || null;
    if (!virtualCategory) {
      return {
        props: {},
        notFound: true
      };
    }
  }
  let navigationVirtualCategories: INavigationVirtualCategory[] | null = null;
  if (offer === 'VOX-PL-ECOMMERCE' && virtualCategories && Array.isArray(virtualCategories)) {
    navigationVirtualCategories = getNavigationVirtualCategories(virtualCategories, path) || null;
  }
  let virtualCategoryFilters: IVirtualCategoryFilters | null = null;
  if (virtualCategory && virtualCategorySlug !== 'products') {
    if (category?.seo) {
      const newSeo = {
        ...category.seo
      } as ICategorySeo;
      if (newSeo?.title && virtualCategory?.meta?.title) {
        newSeo.title = virtualCategory.meta.title;
      }
      if (newSeo?.description && virtualCategory?.meta?.description) {
        newSeo.description = virtualCategory.meta.description;
      }
      category.seo = newSeo;
    }
    if (category?.name && virtualCategory?.title) {
      category.name = virtualCategory.title;
    }
    if (Object.keys(virtualCategory.filters).length > 0 && Object.keys(filters).length === 0) {
      Object.keys(virtualCategory.filters).forEach(key => {
        const value = virtualCategory?.filters ? virtualCategory.filters[key] : undefined;
        if (value) filters[key] = Array.isArray(value) ? value.toString() : value;
      });
      virtualCategoryFilters = virtualCategory.filters;
    }
    if (!sorting && virtualCategory.sort) {
      sorting = virtualCategory.sort;
    }
  }
  if (!virtualCategoryFilters) virtualCategorySlug = null;
  if (revalidationType === 'ListingPageProps' || revalidationType === 'full') {
    categoryPageProps = await getCategoryListingPageProps(foundCategoryId, locale as TLocale, page, filters, sorting, virtualCategory);
    await setCache(`${cacheKey}-ListingPageProps`, categoryPageProps, cachePropsTime, true);
  } else if (Object.keys(filters).length > 0 || sorting) {
    categoryPageProps = await getCategoryListingPageProps(foundCategoryId, locale as TLocale, page, filters, sorting, virtualCategory);
  } else {
    categoryPageProps = await getCache(`${cacheKey}-ListingPageProps`, () => getCategoryListingPageProps(foundCategoryId, locale as TLocale, page, filters, sorting, virtualCategory), cachePropsTime, true);
  }
  let arrangementProducts: IArrangementProduct[] | null = null;
  if (revalidationType === 'ArrangementProducts' || revalidationType === 'full') {
    arrangementProducts = await getArrangementProducts(category);
    await setCache(`${cacheKey}-ArrangementProducts`, arrangementProducts, cachePropsTime, true);
  } else {
    arrangementProducts = await getCache(`${cacheKey}-ArrangementProducts`, () => getArrangementProducts(category), cachePropsTime, true);
  }
  const showProducts = slug[slug.length - 1] === 'products' || isCollection && virtualCategorySlug;
  let pageProps = {
    isCollectionPage: false,
    activeSlug: '',
    ...categoryPageProps,
    arrangementProducts,
    navigationVirtualCategories,
    fullPath,
    content: {},
    showProducts,
    virtualCategoryFilters,
    seo: category.seo,
    virtualCategorySlug
  };
  const activeSlug = category.slug === serviceMapObject.slug ? '' : serviceMapObject.slug;
  if (isCollection) {
    let collectionPageProps: ICollectionPageProps | null = null;
    if (revalidationType === 'CollectionPageProps' || revalidationType === 'full') {
      collectionPageProps = await getCollectionPageProps(category, locale as TLocale, activeSlug);
      await setCache(`${cacheKey}-${activeSlug as string}-CollectionPageProps`, collectionPageProps, cachePropsTime, true);
    } else {
      collectionPageProps = await getCache(`${cacheKey}-${activeSlug as string}-CollectionPageProps`, () => getCollectionPageProps(category, locale as TLocale, activeSlug), cachePropsTime, true);
    }
    pageProps = {
      ...pageProps,
      ...collectionPageProps,
      isCollectionPage: true,
      activeSlug
    };
  }
  try {
    await trackRevalidation(`${fullPath}`, locale as string, REVALIDATION_TIME, 'dynamic', isCollection ? 'collection' : 'category');
  } catch (error) {
    logger.error('Failed to track revalidation:', error);
  }
  return {
    props: {
      ...getDevProps('static', REVALIDATION_TIME),
      ...pageProps,
      ...layoutData,
      slug: category.slug,
      categoryId: category.id,
      categoryName: virtualCategory?.title || getCategoryListingDisplayName(category),
      categoryPageSlug,
      categoryChildren,
      ...(await serverSideTranslations(locale as string)),
      navParams: {
        navTransparency: ENavTransparency.BOTH_TRANSPARENT
      }
    }
  };
};

// biome-ignore lint/suspicious/noExplicitAny: Next.js page props
MainCategoryDynamic.getLayout = function getLayout(page: ReactElement, pageProps: Record<string, any>) {
  return <Layout footer={pageProps.footer} navigationBar={pageProps.navigationBar} categoriesTree={pageProps.categoriesTree} promotionalMessages={pageProps.promotionalMessages} simpleLayout={pageProps.simpleLayout} attachFirstChildToNavigation={pageProps.attachFirstChildToNavigation} navParams={pageProps.navParams} slugToPageScrollY={pageProps.slug}>
      {pageProps.isCollectionPage ? <CollectionLayout categoryName={pageProps.categoryName} categoryChildren={pageProps.categoryChildren} categoryPageContent={pageProps.content} subpages={pageProps.subpages} activeSlug={pageProps.activeSlug} categoryId={pageProps.categoryId} categorySlug={pageProps.categoryPageSlug} categoriesTree={pageProps.categoriesTree} listingData={pageProps.listingData} arrangementProducts={pageProps.arrangementProducts} infoCards={pageProps.infoCards} contentNavigation={pageProps.contentNavigation} categoryBlocks={pageProps.categoryBlocks} virtualCategorySlug={pageProps.virtualCategorySlug} virtualCategoryFilters={pageProps.virtualCategoryFilters} navigationVirtualCategories={pageProps.navigationVirtualCategories}>
          {page}
        </CollectionLayout> : page}
    </Layout>;
};
export default MainCategoryDynamic;